import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import useLenis from "@/lib/useLenis";
import useSeo, { SEO_SITE_URL } from "@/lib/useSeo";
import { useLang } from "@/lib/LangContext";
import { certifications } from "@/lib/certifications";

export default function Certifications() {
  useLenis();
  const { lang } = useLang();
  const c = certifications[lang] || certifications.it;

  const title =
    lang === "en"
      ? "Certifications — Emergent Food"
      : "Certificazioni — Emergent Food";

  useSeo({
    title,
    description: c.subtitle,
    path: "/certifications",
    lang,
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: SEO_SITE_URL },
          { "@type": "ListItem", position: 2, name: "Certifications", item: `${SEO_SITE_URL}/certifications` },
        ],
      },
    ],
  });

  return (
    <div className="App grain min-h-screen bg-[#050505] text-white relative" data-testid="certifications-page">
      <Nav />

      <main>
        {/* Hero */}
        <section className="relative pt-32 lg:pt-44 pb-16 lg:pb-24">
          <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-white/50 hover:text-red-400 transition-colors font-mono-ef mb-12"
              data-testid="cert-back"
            >
              ← Emergent.Food
            </Link>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
              <div className="lg:col-span-7">
                <div className="text-[11px] uppercase tracking-[0.28em] text-red-400 font-mono-ef">
                  — {c.overline}
                </div>
                <h1 className="mt-6 font-display font-black text-5xl sm:text-6xl lg:text-[8vw] leading-[0.9] tracking-[-0.035em]">
                  {c.title}
                </h1>
              </div>
              <div className="lg:col-span-5 lg:pt-4">
                <p className="text-lg text-white/60 leading-relaxed">{c.subtitle}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Active certifications */}
        <section className="py-16 lg:py-24 border-t border-white/10" data-testid="cert-active">
          <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
            <div className="flex items-baseline justify-between mb-10">
              <div className="font-mono-ef text-[10px] uppercase tracking-[0.28em] text-white/40">
                {lang === "en" ? "Currently held" : "Attive oggi"}
              </div>
              <div className="flex items-center gap-2 text-xs text-red-400 font-mono-ef">
                <span className="w-1.5 h-1.5 rounded-full bg-red-400 shadow-[0_0_10px_rgba(239, 68, 68,0.8)]" />
                Active
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {c.active.map((cert, i) => (
                <motion.article
                  key={cert.code}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ delay: i * 0.08, duration: 0.7, ease: [0.19, 1, 0.22, 1] }}
                  data-testid={`cert-active-${cert.code.toLowerCase().replace(/\s+/g, "-")}`}
                  className="card-elev rounded-3xl p-8 lg:p-12 relative overflow-hidden"
                >
                  <div className="flex items-center justify-between mb-8">
                    <span className="font-mono-ef text-[10px] uppercase tracking-[0.24em] text-red-400">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="px-2 py-0.5 rounded-full border border-red-500/40 bg-red-500/10 text-[10px] uppercase tracking-[0.16em] text-red-300 font-mono-ef">
                      Active
                    </span>
                  </div>
                  <h3 className="font-display font-black text-4xl lg:text-5xl tracking-[-0.03em] leading-none">
                    {cert.code}
                  </h3>
                  <div className="mt-3 text-white/70 text-base lg:text-lg font-display font-semibold">
                    {cert.name}
                  </div>
                  <p className="mt-6 text-white/55 text-sm lg:text-base leading-relaxed max-w-lg">
                    {cert.desc}
                  </p>
                  <div className="mt-8 pt-6 border-t border-white/10 font-mono-ef text-[10px] uppercase tracking-[0.2em] text-white/45">
                    {cert.detail}
                  </div>
                </motion.article>
              ))}
            </div>
          </div>
        </section>

        {/* Roadmap */}
        <section className="py-20 lg:py-28 border-t border-white/10" data-testid="cert-roadmap">
          <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
            <div className="flex items-baseline justify-between mb-10">
              <div className="font-mono-ef text-[10px] uppercase tracking-[0.28em] text-white/40">
                {lang === "en" ? "On the roadmap" : "In roadmap"}
              </div>
              <div className="flex items-center gap-2 text-xs text-amber-300/80 font-mono-ef">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                Coming soon
              </div>
            </div>

            <ul className="border-t border-white/10">
              {c.roadmap.map((cert, i) => (
                <motion.li
                  key={cert.code}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ delay: i * 0.06, duration: 0.6 }}
                  className="grid grid-cols-12 items-start gap-4 py-8 lg:py-10 border-b border-white/10"
                >
                  <div className="col-span-2 lg:col-span-1 font-mono-ef text-[10px] tracking-[0.24em] text-white/40 pt-3">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="col-span-10 lg:col-span-4">
                    <h3 className="font-display text-2xl lg:text-3xl font-bold tracking-tight text-white">
                      {cert.code}
                    </h3>
                    <div className="mt-1 text-sm text-white/50">{cert.name}</div>
                  </div>
                  <div className="col-span-12 lg:col-span-6 text-white/60 leading-relaxed text-base lg:text-lg">
                    {cert.desc}
                  </div>
                  <div className="hidden lg:flex col-span-1 justify-end pt-2">
                    <span className="px-2 py-0.5 rounded-full border border-amber-500/40 bg-amber-500/10 text-[9px] uppercase tracking-[0.18em] text-amber-300 font-mono-ef">
                      Soon
                    </span>
                  </div>
                </motion.li>
              ))}
            </ul>
          </div>
        </section>

        {/* Principles */}
        <section className="py-24 lg:py-32 border-t border-white/10 bg-[#070708]">
          <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
            <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[0.9] tracking-[-0.035em] mb-14 lg:mb-20 max-w-3xl">
              {c.principles_title}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6">
              {c.principles.map((p, i) => (
                <motion.div
                  key={p.n}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08, duration: 0.6 }}
                  className="border-t border-white/15 pt-6"
                >
                  <div className="chapter-num text-5xl lg:text-7xl mb-4">{p.n}</div>
                  <h3 className="font-display text-xl lg:text-2xl font-bold tracking-tight">{p.h}</h3>
                  <p className="mt-3 text-sm text-white/55 leading-relaxed">{p.p}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Docs CTA */}
        <section className="py-24 lg:py-32 border-t border-white/10">
          <div className="max-w-[1200px] mx-auto px-6 lg:px-10 text-center">
            <div className="font-mono-ef text-[10px] uppercase tracking-[0.28em] text-red-400 mb-8">
              — {lang === "en" ? "For tenders & audits" : "Per tender e audit"}
            </div>
            <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-7xl tracking-[-0.035em] leading-[0.95]">
              {c.docs_title}
            </h2>
            <p className="mt-6 text-lg text-white/60 max-w-2xl mx-auto leading-relaxed">
              {c.docs_sub}
            </p>
            <Link to="/start" data-testid="cert-cta" className="mt-10 inline-flex btn-pill btn-primary">
              {c.docs_cta}
              <svg className="ml-2" width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path d="M5 12h14M13 5l7 7-7 7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
